import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useEffect, useState } from 'react';
import type {
  CreatePromoCodeInput,
  PromoBenefit,
  PromoCode,
  PromoCoverage,
  PromoDerivedStatus,
  PromoEligibility,
  PromoScope,
  PromoUsage,
} from '@atomfit/shared';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { adminApi, type FranchiseTenant } from '../../api/admin.api.js';
import { useAuth } from '../../hooks/useAuth.js';

interface PromoForm {
  code: string;
  scope: PromoScope;
  tenantId: string;
  usage: PromoUsage;
  maxUsesPerCustomer: string;
  benefit: PromoBenefit;
  discountPercent: string;
  eligibility: PromoEligibility;
  coverage: PromoCoverage;
  coveredSlotCount: string;
  validFrom: string;
  expiresAt: string;
}

const emptyForm: PromoForm = {
  code: '',
  scope: 'PLATFORM',
  tenantId: '',
  usage: 'GLOBAL',
  maxUsesPerCustomer: '1',
  benefit: 'PERCENTAGE',
  discountPercent: '20',
  eligibility: 'ANY_BOOKING',
  coverage: 'WHOLE_RESERVATION',
  coveredSlotCount: '2',
  validFrom: '',
  expiresAt: '',
};

const statusLabels: Record<PromoDerivedStatus, string> = {
  SCHEDULED: translate('pages.admin.admin_promo_codes_page.status_labels.scheduled'),
  ACTIVE: translate('pages.admin.admin_promo_codes_page.status_labels.active'),
  DEACTIVATED: translate('pages.admin.admin_promo_codes_page.status_labels.deactivated'),
  EXPIRED: translate('pages.admin.admin_promo_codes_page.status_labels.expired'),
  EXHAUSTED: translate('pages.admin.admin_promo_codes_page.status_labels.exhausted'),
};

export function AdminPromoCodesPage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [tenants, setTenants] = useState<FranchiseTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<PromoForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PromoDerivedStatus | ''>('');
  const [scopeFilter, setScopeFilter] = useState<PromoScope | ''>('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  async function load() {
    if (!isPlatform) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [promoResponse, firstTenantPage] = await Promise.all([
        adminApi.listPromoCodes({
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(scopeFilter ? { scope: scopeFilter } : {}),
          ...(tenantFilter ? { tenantId: tenantFilter } : {}),
          ...(query.trim() ? { q: query.trim() } : {}),
          page,
          limit: 20,
        }),
        adminApi.listTenants(1, 100),
      ]);
      const remainingPages = Array.from(
        { length: Math.max(0, firstTenantPage.data.meta.totalPages - 1) },
        (_, index) => index + 2,
      );
      const remaining = await Promise.all(
        remainingPages.map((nextPage) => adminApi.listTenants(nextPage, 100)),
      );
      setPromos(promoResponse.data.data);
      setTotalPages(Math.max(1, promoResponse.data.meta.totalPages));
      setTenants(
        [firstTenantPage, ...remaining]
          .flatMap((response) => response.data.data)
          .filter((tenant) => tenant.isActive),
      );
    } catch (err: unknown) {
      setLoadError(
        apiError(err) ??
          translate('pages.admin.admin_promo_codes_page.load.set_load_error.error_promo_code'),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [isPlatform, page, scopeFilter, statusFilter, tenantFilter]);

  function openCreate() {
    setForm(emptyForm);
    setError(null);
    setModalOpen(true);
  }

  async function createPromo(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const payload: CreatePromoCodeInput = {
      code: form.code.trim().toUpperCase(),
      scope: form.scope,
      tenantId: form.scope === 'TENANT' ? form.tenantId : null,
      usage: form.usage,
      maxUsesPerCustomer:
        form.usage === 'GLOBAL' &&
        form.eligibility === 'ANY_BOOKING' &&
        form.benefit === 'PERCENTAGE'
          ? Number(form.maxUsesPerCustomer)
          : 1,
      benefit: form.benefit,
      discountPercent: form.benefit === 'PERCENTAGE' ? Number(form.discountPercent) : null,
      eligibility: form.benefit === 'FIRST_BOOKING_FREE' ? 'FIRST_BOOKING' : form.eligibility,
      coverage: form.coverage,
      coveredSlotCount: form.coverage === 'FIRST_N_SLOTS' ? Number(form.coveredSlotCount) : null,
      validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    };
    try {
      await adminApi.createPromoCode(payload);
      setModalOpen(false);
      setPage(1);
      await load();
    } catch (err: unknown) {
      setError(
        apiError(err) ??
          translate(
            'pages.admin.admin_promo_codes_page.create_promo.set_error.error_promo_code_create',
          ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function togglePromo(promo: PromoCode) {
    try {
      await adminApi.updatePromoCodeStatus(promo.id, !promo.isActive);
      await load();
    } catch (err: unknown) {
      setLoadError(
        apiError(err) ??
          translate(
            'pages.admin.admin_promo_codes_page.toggle_promo.set_load_error.error_promo_code',
          ),
      );
    }
  }

  return (
    <AdminLayout
      title={translate(
        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.admin_layout.promo_code',
      )}
      eyebrow={translate(
        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.admin_layout.eyebrow',
      )}
    >
      {!isPlatform ? (
        <div className="af-card" style={{ padding: 24, color: 'var(--af-muted)' }}>
          {translate(
            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div_card.promo_code',
          )}
        </div>
      ) : (
        <>
          <div className="af-card" style={{ padding: 16, marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>
                  {translate(
                    'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.promo_code',
                  )}
                </div>
                <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 3 }}>
                  {translate(
                    'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.tenant',
                  )}
                </div>
              </div>
              <button
                className="af-primary-btn"
                type="button"
                onClick={openCreate}
                style={{ padding: '12px 18px' }}
              >
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_primary_btn.promo_code_create',
                )}
              </button>
            </div>
          </div>

          <div className="af-card" style={{ padding: 14, marginBottom: 16 }}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setPage(1);
                void load();
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(180px, 1fr) repeat(3, minmax(150px, auto)) auto',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <label className="af-label">
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.search',
                )}
                <input
                  className="af-input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={translate(
                    'pages.admin.admin_promo_codes_page.admin_promo_codes_page.input_input.placeholder_content',
                  )}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.status',
                )}
                <CustomSelect
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value as PromoDerivedStatus | '');
                    setPage(1);
                  }}
                  options={[
                    {
                      value: '',
                      label: translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options',
                      ),
                    },
                    ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
                  ]}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.scope',
                )}
                <CustomSelect
                  value={scopeFilter}
                  onChange={(value) => {
                    setScopeFilter(value as PromoScope | '');
                    setPage(1);
                  }}
                  options={[
                    {
                      value: '',
                      label: translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_2',
                      ),
                    },
                    {
                      value: 'PLATFORM',
                      label: translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_3',
                      ),
                    },
                    {
                      value: 'TENANT',
                      label: translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_4',
                      ),
                    },
                  ]}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.tenant',
                )}
                <CustomSelect
                  value={tenantFilter}
                  onChange={(value) => {
                    setTenantFilter(value);
                    setPage(1);
                  }}
                  options={[
                    {
                      value: '',
                      label: translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_5',
                      ),
                    },
                    ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
                  ]}
                />
              </label>
              <button className="af-outline-btn" type="submit" style={{ padding: '11px 14px' }}>
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.search',
                )}
              </button>
            </form>
          </div>

          <div className="af-card" style={{ overflow: 'auto' }}>
            {loading ? (
              <div
                style={{
                  padding: 24,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  color: 'var(--af-muted)',
                }}
              >
                <Spinner />{' '}
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.loading_promo_code',
                )}
              </div>
            ) : loadError ? (
              <div style={{ padding: 24 }}>
                <div className="af-error" style={{ marginBottom: 14 }}>
                  {loadError}
                </div>
                <button className="af-outline-btn" type="button" onClick={() => void load()}>
                  {translate(
                    'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.retry',
                  )}
                </button>
              </div>
            ) : promos.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--af-muted)' }}>
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.promo_code_2',
                )}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                    {[
                      translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.tr.code',
                      ),
                      translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.tr.scope',
                      ),
                      translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.tr.benefit',
                      ),
                      translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.tr.copy',
                      ),
                      translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.tr.validity',
                      ),
                      translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.tr.status',
                      ),
                      '',
                    ].map((heading) => (
                      <th
                        key={heading}
                        style={{
                          padding: '12px 14px',
                          textAlign: 'left',
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          color: 'var(--af-dim)',
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {promos.map((promo) => (
                    <tr key={promo.id} style={{ borderBottom: '1px solid rgba(124,118,106,0.12)' }}>
                      <td style={{ padding: '13px 14px' }}>
                        <div className="af-mono" style={{ fontWeight: 900 }}>
                          {promo.code}
                        </div>
                        <div style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 4 }}>
                          {promo.coverage === 'WHOLE_RESERVATION'
                            ? translate(
                                'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.booking',
                              )
                            : promo.coverage === 'FIRST_SLOT'
                              ? translate(
                                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.slot',
                                )
                              : translate(
                                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.slots',
                                  { value1: promo.coveredSlotCount },
                                )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        {promo.scope === 'PLATFORM'
                          ? translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.td.copy',
                            )
                          : (promo.tenant?.name ??
                            translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.td.tenant',
                            ))}
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <div style={{ fontWeight: 800 }}>
                          {promo.benefit === 'FIRST_BOOKING_FREE'
                            ? translate(
                                'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.booking_2',
                              )
                            : translate(
                                'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.discount',
                                { value1: promo.discountPercent },
                              )}
                        </div>
                        {promo.benefit === 'PERCENTAGE' && (
                          <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                            {promo.eligibility === 'FIRST_BOOKING'
                              ? translate(
                                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.booking_3',
                                )
                              : translate(
                                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.booking_4',
                                )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <div>
                          {promo.usage === 'GLOBAL'
                            ? translate(
                                'pages.admin.admin_promo_codes_page.usage.per_customer_summary',
                                { count: promo.maxUsesPerCustomer },
                              )
                            : translate(
                                'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.total',
                              )}
                        </div>
                        <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                          {promo.redeemedCount}{' '}
                          {translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.copy',
                          )}{' '}
                          {promo.reservedCount}{' '}
                          {translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.copy_2',
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <div>
                          {formatDate(promo.validFrom) ||
                            translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.copy_3',
                            )}
                        </div>
                        <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                          {translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.copy_4',
                          )}{' '}
                          {formatDate(promo.expiresAt) ||
                            translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.div.copy_5',
                            )}
                        </div>
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <Tag
                          {...(promo.derivedStatus === 'ACTIVE'
                            ? { color: 'var(--af-success)' }
                            : {})}
                        >
                          {statusLabels[promo.derivedStatus]}
                        </Tag>
                      </td>
                      <td style={{ padding: '13px 14px', textAlign: 'right' }}>
                        <button
                          className="af-outline-btn"
                          type="button"
                          disabled={
                            promo.derivedStatus === 'EXPIRED' || promo.derivedStatus === 'EXHAUSTED'
                          }
                          onClick={() => void togglePromo(promo)}
                          style={{ padding: '8px 10px' }}
                        >
                          {promo.isActive
                            ? translate(
                                'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.label',
                              )
                            : translate(
                                'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.label_2',
                              )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
                marginTop: 16,
              }}
            >
              <button
                className="af-outline-btn"
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.previous',
                )}
              </button>
              <span style={{ color: 'var(--af-muted)' }}>
                {page} {'/'} {totalPages}
              </span>
              <button
                className="af-outline-btn"
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                {translate(
                  'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.next',
                )}
              </button>
            </div>
          )}

          {modalOpen && (
            <div
              className="af-profile-modal-backdrop"
              role="presentation"
              onClick={() => setModalOpen(false)}
            >
              <form
                className="af-profile-modal"
                onSubmit={(event) => void createPromo(event)}
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: 'min(760px, calc(100vw - 32px))',
                  maxHeight: 'calc(100vh - 60px)',
                  overflow: 'auto',
                }}
              >
                <h2 style={{ margin: '0 0 16px', fontSize: 22 }}>
                  {translate(
                    'pages.admin.admin_promo_codes_page.admin_promo_codes_page.h2.promo_code_create',
                  )}
                </h2>
                {error && (
                  <div className="af-error" style={{ marginBottom: 14 }}>
                    {error}
                  </div>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 12,
                  }}
                >
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.code',
                    )}

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        gap: 8,
                      }}
                    >
                      <input
                        className="af-input af-mono"
                        value={form.code}
                        minLength={3}
                        maxLength={32}
                        pattern="[A-Za-z0-9-]+"
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            code: event.target.value.toUpperCase(),
                          }))
                        }
                        required
                      />

                      <button
                        className="af-outline-btn"
                        type="button"
                        onClick={() =>
                          setForm((current) => ({ ...current, code: generatePromoCode() }))
                        }
                        style={{ padding: '0 12px', whiteSpace: 'nowrap' }}
                      >
                        {translate(
                          'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.label_3',
                        )}
                      </button>
                    </div>
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.scope_2',
                    )}
                    <CustomSelect
                      value={form.scope}
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          scope: value as PromoScope,
                          tenantId: value === 'PLATFORM' ? '' : current.tenantId,
                        }))
                      }
                      options={[
                        {
                          value: 'PLATFORM',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_6',
                          ),
                        },
                        {
                          value: 'TENANT',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_7',
                          ),
                        },
                      ]}
                    />
                  </label>
                  {form.scope === 'TENANT' && (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.tenant_2',
                      )}
                      <CustomSelect
                        value={form.tenantId}
                        onChange={(value) =>
                          setForm((current) => ({ ...current, tenantId: value }))
                        }
                        options={[
                          {
                            value: '',
                            label: translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_8',
                            ),
                          },
                          ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
                        ]}
                      />
                    </label>
                  )}
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.copy',
                    )}
                    <CustomSelect
                      value={form.usage}
                      onChange={(value) =>
                        setForm((current) => ({ ...current, usage: value as PromoUsage }))
                      }
                      options={[
                        {
                          value: 'GLOBAL',
                          label: translate('pages.admin.admin_promo_codes_page.usage.per_customer'),
                        },
                        {
                          value: 'SINGLE_USE',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_10',
                          ),
                        },
                      ]}
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.benefit',
                    )}
                    <CustomSelect
                      value={form.benefit}
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          benefit: value as PromoBenefit,
                          eligibility:
                            value === 'FIRST_BOOKING_FREE' ? 'FIRST_BOOKING' : current.eligibility,
                        }))
                      }
                      options={[
                        {
                          value: 'PERCENTAGE',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_11',
                          ),
                        },
                        {
                          value: 'FIRST_BOOKING_FREE',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_12',
                          ),
                        },
                      ]}
                    />
                  </label>
                  {form.usage === 'GLOBAL' &&
                    form.eligibility === 'ANY_BOOKING' &&
                    form.benefit === 'PERCENTAGE' && (
                      <label className="af-label">
                        {translate('pages.admin.admin_promo_codes_page.usage.customer_limit')}
                        <input
                          className="af-input"
                          type="number"
                          min={1}
                          max={2_147_483_647}
                          step={1}
                          required
                          value={form.maxUsesPerCustomer}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              maxUsesPerCustomer: event.target.value,
                            }))
                          }
                        />
                      </label>
                    )}
                  {form.benefit === 'PERCENTAGE' && (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.discount',
                      )}
                      <input
                        className="af-input"
                        type="number"
                        min={1}
                        max={99}
                        value={form.discountPercent}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            discountPercent: event.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                  )}
                  {form.benefit === 'PERCENTAGE' && (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.copy_2',
                      )}
                      <CustomSelect
                        value={form.eligibility}
                        onChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            eligibility: value as PromoEligibility,
                          }))
                        }
                        options={[
                          {
                            value: 'ANY_BOOKING',
                            label: translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_13',
                            ),
                          },
                          {
                            value: 'FIRST_BOOKING',
                            label: translate(
                              'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_14',
                            ),
                          },
                        ]}
                      />
                    </label>
                  )}
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.coverage',
                    )}
                    <CustomSelect
                      value={form.coverage}
                      onChange={(value) =>
                        setForm((current) => ({ ...current, coverage: value as PromoCoverage }))
                      }
                      options={[
                        {
                          value: 'WHOLE_RESERVATION',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_15',
                          ),
                        },
                        {
                          value: 'FIRST_SLOT',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_16',
                          ),
                        },
                        {
                          value: 'FIRST_N_SLOTS',
                          label: translate(
                            'pages.admin.admin_promo_codes_page.admin_promo_codes_page.custom_select.options_17',
                          ),
                        },
                      ]}
                    />
                  </label>
                  {form.coverage === 'FIRST_N_SLOTS' && (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.slot_count',
                      )}
                      <input
                        className="af-input"
                        type="number"
                        min={2}
                        max={24}
                        value={form.coveredSlotCount}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            coveredSlotCount: event.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                  )}
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.validity',
                    )}
                    <input
                      className="af-input"
                      type="datetime-local"
                      value={form.validFrom}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, validFrom: event.target.value }))
                      }
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.label_label.validity_2',
                    )}
                    <input
                      className="af-input"
                      type="datetime-local"
                      value={form.expiresAt}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, expiresAt: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}
                >
                  <button
                    className="af-outline-btn"
                    type="button"
                    onClick={() => setModalOpen(false)}
                  >
                    {translate(
                      'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_outline_btn.cancel',
                    )}
                  </button>
                  <button
                    className="af-primary-btn"
                    type="submit"
                    disabled={saving || (form.scope === 'TENANT' && !form.tenantId)}
                  >
                    {saving
                      ? translate(
                          'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_primary_btn.label',
                        )
                      : translate(
                          'pages.admin.admin_promo_codes_page.admin_promo_codes_page.button_primary_btn.create',
                        )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

function formatDate(value: Date | string | null) {
  if (!value) return '';
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

function generatePromoCode(length = 10) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}

function apiError(err: unknown) {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const direct = (data as { message?: unknown }).message;
  if (typeof direct === 'string') return direct;
  const nested = (data as { error?: { message?: unknown } }).error?.message;
  return typeof nested === 'string' ? nested : null;
}
